import { useMemo, useState } from "react";
import { FAMILY_PAY, TAX_YEAR, gbp } from "@/lib/statutory";

export function MaternityPayCalculator() {
  const [weeklyEarnings, setWeeklyEarnings] = useState(500);
  const [weeksToTake, setWeeksToTake] = useState(39);

  const result = useMemo(() => {
    const higherWeeks = Math.min(weeksToTake, FAMILY_PAY.smpHigherRateWeeks);
    const standardWeeks = Math.max(
      0,
      Math.min(weeksToTake, FAMILY_PAY.smpTotalWeeks) - higherWeeks,
    );
    const higherRate = weeklyEarnings * FAMILY_PAY.smpHigherRatePercent;
    const standardRate = Math.min(FAMILY_PAY.standardWeeklyRate, higherRate);
    const total = higherWeeks * higherRate + standardWeeks * standardRate;
    return { higherWeeks, standardWeeks, higherRate, standardRate, total };
  }, [weeklyEarnings, weeksToTake]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <NumField label="Average weekly earnings (£)" value={weeklyEarnings} onChange={setWeeklyEarnings} min={0} />
        <NumField label="Weeks of SMP to take" value={weeksToTake} onChange={setWeeksToTake} min={0} max={FAMILY_PAY.smpTotalWeeks} />
      </div>

      <div className="mt-8 rounded-2xl bg-brand p-6 text-brand-foreground">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">
          Estimated Statutory Maternity Pay
        </p>
        <p className="mt-3 font-serif text-5xl">{gbp(result.total, { decimals: 2 })}</p>
        <p className="mt-3 text-sm text-brand-foreground/70">
          {result.higherWeeks} weeks at {gbp(result.higherRate, { decimals: 2 })}/week (90% of earnings), then{" "}
          {result.standardWeeks} weeks at {gbp(result.standardRate, { decimals: 2 })}/week ({TAX_YEAR} standard rate).
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            SMP runs for up to {FAMILY_PAY.smpTotalWeeks} weeks. The first {FAMILY_PAY.smpHigherRateWeeks} weeks are paid
            at {Math.round(FAMILY_PAY.smpHigherRatePercent * 100)}% of average weekly earnings. The remaining 33 weeks
            are paid at the lower of {gbp(FAMILY_PAY.standardWeeklyRate, { decimals: 2 })} or 90% of earnings.
          </p>
          <p>
            The employee must have 26 weeks' service by the qualifying week and earn at least the Lower Earnings Limit.
          </p>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, min, max }: { label: string; value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
